import React from 'react';
import {
  audienceHasIcp,
  icpMatrixHasValues,
  icpMatrixRows,
  summarizeIcp,
  type CampaignAudience,
  type IcpMatrixRow,
} from './campaignAudience';
import HelpBadge from './components/HelpBadge';
import { CrmTermHelp } from './CrmTermText';

type Campaign = {
  id: number;
  name: string;
  channel: string;
  audience?: CampaignAudience | null;
};

type SearchResult = {
  searches?: Array<{ query?: string; provider?: string; hit_count?: number }>;
  context_urls?: string[];
  created_count?: number;
  tam_created_count?: number;
  skipped_existing?: number;
  total_active?: number;
  message?: string | null;
  phase?: string;
};

type DeriveResult = {
  matrix?: IcpMatrixRow[];
  searches?: SearchResult['searches'];
  context_urls?: string[];
  notes?: string | null;
};

export const LEAD_SEARCH_PHASES = [
  { id: 'companies', en: 'Company websites', es: 'Sitios de empresas' },
  { id: 'industry', en: 'Industry docs & practices', es: 'Documentación e industria' },
  { id: 'competitors', en: 'Competitor sites', es: 'Sitios de competidores' },
] as const;

export const LEAD_SEARCH_LIMITS = [5, 10, 20, 50] as const;
export type LeadSearchPhase = (typeof LEAD_SEARCH_PHASES)[number]['id'];
export type LeadSearchLimit = (typeof LEAD_SEARCH_LIMITS)[number];

type Props = {
  spanish: boolean;
  busy: boolean;
  campaigns: Campaign[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onBuildIcp: (campaignId: number, description: string) => Promise<DeriveResult | void> | DeriveResult | void;
  onSaveMatrix: (
    campaignId: number,
    description: string,
    matrix: IcpMatrixRow[],
  ) => Promise<void> | void;
  onSearchLeads: (
    campaignId: number,
    description: string,
    matrix: IcpMatrixRow[],
    phase: LeadSearchPhase,
    limit: LeadSearchLimit,
  ) => Promise<SearchResult | void> | SearchResult | void;
};

const outboundCampaigns = (campaigns: Campaign[]): Campaign[] =>
  campaigns.filter((row) => row.channel === 'outbound');

const searchStatus = (result: SearchResult | void, spanish: boolean): string => {
  const searches = result?.searches || [];
  const providers = [...new Set(searches.map((row) => row.provider).filter(Boolean))];
  const hits = searches.reduce((sum, row) => sum + Number(row.hit_count || 0), 0);
  const created = Number(result?.created_count || 0);
  const tam = Number(result?.tam_created_count || 0);
  const skipped = Number(result?.skipped_existing || 0);
  const total = Number(result?.total_active || 0);
  const pages = (result?.context_urls || []).length;
  const phase = LEAD_SEARCH_PHASES.find((row) => row.id === result?.phase);
  const phaseLabel = phase ? (spanish ? phase.es : phase.en) : '';
  const query = String(searches[0]?.query || '').trim();
  const queryHint = hits === 0 && query ? (spanish ? ` Consulta: ${query}.` : ` Query: ${query}.`) : '';
  if (spanish) {
    return `Búsqueda ${phaseLabel || providers.join(', ') || 'web'}: ${hits} resultados, ${pages} páginas, ${tam} empresas TAM, ${created} leads con email${
      total ? `, ${total} listos` : ''
    }${skipped ? `, ${skipped} ya en el libro` : ''}.${result?.message ? ` ${result.message}` : ''}${queryHint}`;
  }
  return `Searched ${phaseLabel || providers.join(', ') || 'the web'}: ${hits} hits, ${pages} context page${
    pages === 1 ? '' : 's'
  }, ${tam} TAM compan${tam === 1 ? 'y' : 'ies'}, ${created} named lead${created === 1 ? '' : 's'}${
    skipped ? `, ${skipped} already in the book` : ''
  }${total ? `, ${total} sales-ready` : ''}.${result?.message ? ` ${result.message}` : ''}${queryHint}`;
};

const LeadIcpAccordion: React.FC<Props> = ({
  spanish,
  busy,
  campaigns,
  selectedId,
  onSelect,
  onBuildIcp,
  onSaveMatrix,
  onSearchLeads,
}) => {
  const options = outboundCampaigns(campaigns);
  const selected = options.find((row) => row.id === selectedId) || options[0] || null;
  const savedDescription = String(selected?.audience?.description || '');
  const [open, setOpen] = React.useState(false);
  const [description, setDescription] = React.useState(savedDescription);
  const [matrix, setMatrix] = React.useState<IcpMatrixRow[]>(() => icpMatrixRows(selected?.audience));
  const [phase, setPhase] = React.useState<LeadSearchPhase>('companies');
  const [batchSize, setBatchSize] = React.useState<LeadSearchLimit>(10);
  const [status, setStatus] = React.useState('');
  const [history, setHistory] = React.useState<string[]>([]);

  React.useEffect(() => {
    setDescription(String(selected?.audience?.description || ''));
    setMatrix(icpMatrixRows(selected?.audience));
    setStatus('');
    setHistory([]);
    setPhase('companies');
  }, [selected?.id]);

  React.useEffect(() => {
    setDescription(String(selected?.audience?.description || ''));
  }, [selected?.audience?.description]);

  if (!selected) return null;

  const workingAudience: CampaignAudience = { ...selected.audience, description, matrix };
  const summary = summarizeIcp(selected.audience, spanish);
  const hasMatrix = icpMatrixHasValues({ matrix });
  const placeholder = spanish
    ? 'Describa a quién vende, el precio que pueden pagar, y opcionalmente “lea ejemplo.com” para contexto del producto.'
    : 'Describe who you sell to, what they can pay, and optionally “read example.com” for product context.';

  const setRowValue = (key: string, value: string) => {
    setMatrix((rows) => rows.map((row) => (row.key === key ? { ...row, value } : row)));
  };

  const build = async () => {
    const text = description.trim();
    if (!text) return;
    setStatus(spanish ? 'Parseando la descripción y buscando señales de ICP…' : 'Parsing the description and searching the web for ICP signals…');
    try {
      const result = await onBuildIcp(selected.id, text);
      if (result?.matrix?.length) setMatrix(icpMatrixRows({ matrix: result.matrix }));
      const pages = (result?.context_urls || []).length;
      const hits = (result?.searches || []).reduce((sum, row) => sum + Number(row.hit_count || 0), 0);
      setStatus(
        spanish
          ? `Matriz lista (${hits} resultados, ${pages} páginas). Revísela y luego busque leads.`
          : `ICP matrix ready (${hits} hits, ${pages} context page${pages === 1 ? '' : 's'}). Review it, then search for leads.`,
      );
    } catch (error) {
      setStatus(error instanceof Error ? error.message : spanish ? 'No se pudo armar la matriz.' : 'Could not build the ICP matrix.');
    }
  };

  const save = async () => {
    try {
      await onSaveMatrix(selected.id, description.trim(), matrix);
      setStatus(spanish ? 'ICP guardado. Ya puede buscar leads.' : 'ICP saved. You can search for leads when ready.');
    } catch (error) {
      setStatus(error instanceof Error ? error.message : spanish ? 'No se pudo guardar el ICP.' : 'Could not save the ICP.');
    }
  };

  const search = async () => {
    setStatus(
      spanish
        ? phase === 'industry'
          ? 'Buscando documentación e industria…'
          : phase === 'competitors'
            ? 'Buscando sitios de competidores…'
            : 'Buscando empresas en la web…'
        : phase === 'industry'
          ? 'Searching industry documentation…'
          : phase === 'competitors'
            ? 'Searching competitor sites…'
            : 'Searching company websites…',
    );
    try {
      const result = await onSearchLeads(selected.id, description.trim(), matrix, phase, batchSize);
      const line = searchStatus(result, spanish);
      setStatus(line);
      setHistory((rows) => [line, ...rows].slice(0, 20));
    } catch (error) {
      setStatus(error instanceof Error ? error.message : spanish ? 'La búsqueda falló.' : 'Search failed.');
    }
  };

  return (
    <section className="pursuit-settings-accordion pursuit-icp-accordion" aria-label={spanish ? 'ICP de la campaña' : 'Campaign ICP'}>
      <div className="pursuit-icp-campaign-row">
        <label>
          {spanish ? 'Campaña' : 'Campaign'}
          <select
            aria-label={spanish ? 'ICP de campaña' : 'Campaign ICP'}
            value={selected.id}
            onChange={(event) => onSelect(Number(event.target.value))}
          >
            {options.map((row) => (
              <option key={row.id} value={row.id}>
                {row.name}
                {row.audience?.product ? ` · ${row.audience.product}` : ''}
              </option>
            ))}
          </select>
        </label>
      </div>
      <h5 className="pursuit-settings-accordion-heading">
        <button
          type="button"
          className="pursuit-settings-accordion-trigger"
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <span>ICP</span>
          {!open && summary ? <em>{summary}</em> : null}
        </button>
        <CrmTermHelp term="icp" spanish={spanish} />
      </h5>
      {open ? (
        <div className="pursuit-settings-accordion-body">
          <label className="pursuit-icp-description">
            {spanish ? 'A quién vende' : 'Who you sell to'}
            <textarea
              aria-label={spanish ? 'A quién vende' : 'Who you sell to'}
              rows={5}
              value={description}
              placeholder={placeholder}
              onChange={(event) => setDescription(event.target.value)}
            />
          </label>
          <div className="pursuit-project-editor-actions">
            <button
              type="button"
              className="dashboard-btn dashboard-btn-primary"
              disabled={busy || !description.trim()}
              onClick={() => void build()}
            >
              {spanish ? 'Armar matriz ICP' : 'Build ICP matrix'}
            </button>
          </div>
          <div className="pursuit-icp-matrix-wrap">
            <p className="pursuit-icp-matrix-label">{spanish ? 'Matriz ICP (editable)' : 'ICP matrix (editable)'}</p>
            <table className="pursuit-icp-matrix" aria-label={spanish ? 'Matriz ICP' : 'ICP matrix'}>
              <thead>
                <tr>
                  <th>{spanish ? 'Señal' : 'Signal'}</th>
                  <th>{spanish ? 'Valor' : 'Value'}</th>
                </tr>
              </thead>
              <tbody>
                {matrix.map((row) => (
                  <tr key={row.key}>
                    <th scope="row">{row.label}</th>
                    <td>
                      <input
                        aria-label={row.label}
                        value={row.value}
                        onChange={(event) => setRowValue(row.key, event.target.value)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pursuit-icp-search-row">
            <label>
              <span className="pursuit-icp-field-label">
                {spanish ? 'Dónde buscar' : 'Where to look'}
                <HelpBadge label={spanish ? 'Qué significa dónde buscar' : 'What where to look means'}>
                  {spanish
                    ? 'No es una frase de búsqueda. Cada clic busca un tipo de sitio: homepages de empresas, documentación de la industria, o sitios de competidores.'
                    : 'Not a search phrase. Each click searches a different kind of site: company homepages, industry documentation, or competitor sites.'}
                </HelpBadge>
              </span>
              <select
                aria-label={spanish ? 'Fase de búsqueda de leads' : 'Lead search phase'}
                value={phase}
                onChange={(event) => setPhase(event.target.value as LeadSearchPhase)}
              >
                {LEAD_SEARCH_PHASES.map((row) => (
                  <option key={row.id} value={row.id}>
                    {spanish ? row.es : row.en}
                  </option>
                ))}
              </select>
            </label>
            <label>
              {spanish ? 'Empresas por clic' : 'Companies per click'}
              <select
                aria-label={spanish ? 'Empresas por clic' : 'Companies per click'}
                value={batchSize}
                onChange={(event) => setBatchSize(Number(event.target.value) as LeadSearchLimit)}
              >
                {LEAD_SEARCH_LIMITS.map((row) => (
                  <option key={row} value={row}>
                    {row}
                  </option>
                ))}
              </select>
            </label>
            <div className="pursuit-project-editor-actions">
              <button type="button" className="dashboard-btn" disabled={busy} onClick={() => void save()}>
                {spanish ? 'Guardar ICP' : 'Save ICP'}
              </button>
              <button
                type="button"
                className="dashboard-btn dashboard-btn-primary"
                disabled={busy || !hasMatrix}
                onClick={() => void search()}
              >
                {spanish ? 'Buscar empresas' : 'Search companies'}
              </button>
            </div>
          </div>
          {status ? (
            <p className="pursuit-icp-mapped" role="status">
              {status}
            </p>
          ) : null}
          {history.length > 1 ? (
            <ol className="pursuit-icp-search-history" aria-label={spanish ? 'Historial de búsquedas' : 'Search history'}>
              {history.slice(1).map((line, index) => (
                <li key={`${index}-${line.slice(0, 24)}`}>{line}</li>
              ))}
            </ol>
          ) : null}
          {!audienceHasIcp(workingAudience) ? (
            <p className="pursuit-icp-hint">
              {spanish
                ? 'Aún no hay un ICP guardado para esta campaña.'
                : 'This campaign does not have a saved ICP yet.'}
            </p>
          ) : null}
        </div>
      ) : null}
    </section>
  );
};

export default LeadIcpAccordion;
